import { Link, useLocation } from '@tanstack/react-router'
import { useRef, useState } from 'react'

import { MoreIcon } from '@/components/icons'
import { cx } from '@/lib/cx'
import { useT } from '@/lib/i18n/translate'
import { type NavTarget, barSlots, isItemActive } from '@/lib/nav'
import { useDismiss } from '@/lib/use-dismiss'

/**
 * Small screens (T13): 4 slots — all items when ≤4, else top 3 + More opening a
 * bottom sheet that holds only the overflow destinations.
 */
export function BottomBar({ items }: { items: readonly NavTarget[] }) {
  const t = useT()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(open, ref, () => setOpen(false))
  if (items.length === 0) return null

  const { bar, overflow } = barSlots(items)
  const moreActive = overflow.some((item) => isItemActive(pathname, item))
  const slot = 'flex min-h-tap flex-col items-center justify-center gap-0.5 py-1 text-xs'

  return (
    <div ref={ref} className="md:hidden">
      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('nav.more')}
            className="bg-surface fixed inset-x-0 bottom-0 z-50 rounded-t-xl p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-lg"
          >
            {overflow.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                params={item.params ?? {}}
                onClick={() => setOpen(false)}
                activeOptions={{ exact: item.exact ?? false }}
                activeProps={{ className: 'bg-accent text-accent-fg' }}
                inactiveProps={{ className: 'hover:bg-accent-soft' }}
                className="flex min-h-tap items-center gap-3 rounded-lg px-3"
              >
                <item.icon />
                <span className="truncate">{t(item.label)}</span>
              </Link>
            ))}
          </div>
        </>
      )}
      <nav
        aria-label={t('nav.label')}
        className="bg-surface border-border fixed inset-x-0 bottom-0 z-30 grid auto-cols-fr grid-flow-col border-t pb-[env(safe-area-inset-bottom)] print:hidden"
      >
        {bar.map((item) => (
          <Link
            key={item.key}
            to={item.to}
            params={item.params ?? {}}
            activeOptions={{ exact: item.exact ?? false }}
            activeProps={{ className: 'text-accent-ink' }}
            inactiveProps={{ className: 'text-muted' }}
            className={slot}
          >
            <item.icon />
            <span className="max-w-full truncate">{t(item.label)}</span>
          </Link>
        ))}
        {overflow.length > 0 && (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className={cx(slot, moreActive ? 'text-accent-ink' : 'text-muted')}
          >
            <MoreIcon />
            <span>{t('nav.more')}</span>
          </button>
        )}
      </nav>
    </div>
  )
}
